import { useState, useEffect } from 'react';
import { ArrowRight, Calendar, MapPin, Clock, ExternalLink, Sparkles, TrendingUp, Heart } from 'lucide-react';
import { useScrollReveal } from '../hooks/useScrollReveal';
import { ContactFormModal } from '../components/ContactFormModal';
import { SEOHead, EventStructuredData } from '../components/SEOHead';
import axios from 'axios';
import { API_BASE } from '../config/api';

const API = API_BASE;

const CATEGORIES = [
  { key: '', label: 'All Events' },
  { key: 'leadership', label: 'Leadership' },
  { key: 'personal_growth', label: 'Personal Growth' },
  { key: 'business', label: 'Business' },
];

const CATEGORY_STYLES = {
  leadership: { bg: 'bg-[#FFE8F0]', text: 'text-[#D13A7A]', icon: TrendingUp },
  personal_growth: { bg: 'bg-[#F0E6F6]', text: 'text-[#9B4FBF]', icon: Heart },
  business: { bg: 'bg-[#FFE8F0]', text: 'text-[#D13A7A]', icon: Sparkles },
};

const formatDate = (dateStr) => {
  const d = new Date(dateStr + 'T00:00:00');
  return d.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' });
};

export const EventsPage = () => {
  const pageRef = useScrollReveal();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('');
  const [modal, setModal] = useState({ open: false, type: 'booking_call' });

  useEffect(() => {
    axios.get(`${API}/api/events${filter ? `?category=${filter}` : ''}`)
      .then(({ data }) => setEvents(data.items))
      .finally(() => setLoading(false));
  }, [filter]);

  const openForm = (type) => setModal({ open: true, type });

  const upcoming = events.filter(e => e.status === 'upcoming');
  const past = events.filter(e => e.status === 'past');

  return (
    <main className="pt-24" ref={pageRef}>
      <SEOHead path="/events" />
      <EventStructuredData events={upcoming} />
      <ContactFormModal isOpen={modal.open} onClose={() => setModal({ open: false, type: 'booking_call' })} inquiryType={modal.type} sourcePage="events" />

      {/* Hero */}
      <section className="py-16 md:py-24 bg-cream relative overflow-hidden" data-testid="events-hero">
        <div className="absolute top-1/2 right-0 w-[500px] h-[500px] bg-[#E84993]/4 rounded-full blur-[100px] -translate-y-1/2" />
        <div className="container-padding relative">
          <div className="max-w-3xl">
            <span className="reveal overline-text mb-4 block">Experiences & Events</span>
            <h1 className="reveal reveal-delay-1 text-4xl md:text-5xl lg:text-6xl font-medium leading-[1.1] mb-6" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>
              Transformative <span className="text-highlight">Experiences</span>
            </h1>
            <p className="reveal reveal-delay-2 body-text max-w-xl mb-8">
              Premium leadership forums, personal growth retreats, and professional development experiences designed to elevate, empower, and transform.
            </p>
            <div className="reveal reveal-delay-3">
              <button onClick={() => openForm('speaking')} className="btn-primary arrow-hover" data-testid="events-hero-cta">
                Inquire About Events <ArrowRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Filter */}
      <section className="py-8 bg-white border-b border-[#F0E6F6]" data-testid="events-filter">
        <div className="container-padding">
          <div className="flex flex-wrap gap-3">
            {CATEGORIES.map(cat => (
              <button
                key={cat.key}
                onClick={() => { setLoading(true); setFilter(cat.key); }}
                className={`px-5 py-2.5 text-xs font-medium tracking-wider uppercase rounded-full border transition-all duration-300 ${
                  filter === cat.key
                    ? 'bg-[#E84993] text-white border-[#E84993]'
                    : 'bg-white text-[#6A6A6A] border-[#F0E6F6] hover:border-[#E84993] hover:text-[#E84993]'
                }`}
                data-testid={`filter-${cat.key || 'all'}`}
              >
                {cat.label}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Upcoming Events */}
      {upcoming.length > 0 && (
        <section className="section-padding bg-white" data-testid="upcoming-events">
          <div className="container-padding">
            <div className="mb-12">
              <span className="reveal overline-text mb-3 block">Upcoming</span>
              <h2 className="reveal reveal-delay-1 heading-lg">Upcoming Events</h2>
              <div className="reveal reveal-delay-1 divider-gradient w-24 mt-5" />
            </div>

            {/* Featured event (first) */}
            <div className="reveal mb-10">
              <EventCardFeatured event={upcoming[0]} />
            </div>

            {/* Grid for remaining */}
            {upcoming.length > 1 && (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {upcoming.slice(1).map((ev, i) => (
                  <div key={ev.id || i} className={`reveal ${i > 0 ? `reveal-delay-${Math.min(i, 3)}` : ''}`}>
                    <EventCard event={ev} />
                  </div>
                ))}
              </div>
            )}
          </div>
        </section>
      )}

      {/* Loading */}
      {loading && (
        <div className="py-20 flex justify-center">
          <div className="w-8 h-8 border-2 border-[#E84993] border-t-transparent rounded-full animate-spin" />
        </div>
      )}

      {/* Empty */}
      {!loading && events.length === 0 && (
        <div className="py-20 text-center">
          <p className="text-[#9A9A9A] text-lg">No events found for this category.</p>
        </div>
      )}

      {/* Past Events */}
      {past.length > 0 && (
        <section className="section-padding bg-cream" data-testid="past-events">
          <div className="container-padding">
            <div className="mb-12">
              <span className="reveal overline-text mb-3 block">Previous</span>
              <h2 className="reveal reveal-delay-1 heading-lg">Past Events</h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {past.map((ev, i) => (
                <div key={ev.id || i} className={`reveal ${i > 0 ? `reveal-delay-${Math.min(i, 3)}` : ''}`}>
                  <EventCard event={ev} isPast />
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Avenues of Excellence Partnership */}
      <section className="py-16 md:py-20 bg-white" data-testid="avenues-section">
        <div className="container-padding">
          <div className="reveal max-w-4xl mx-auto bg-cream rounded-3xl p-10 md:p-14 relative overflow-hidden border border-[#F0E6F6]">
            <div className="absolute top-0 right-0 w-64 h-64 bg-[#E84993]/4 rounded-full blur-[80px]" />
            <div className="relative text-center">
              <span className="overline-text mb-4 block">Strategic Partnership</span>
              <h2 className="text-2xl md:text-3xl lg:text-4xl font-medium mb-5" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>
                Avenues of Excellence
              </h2>
              <div className="divider-gradient w-20 mx-auto mb-6" />
              <p className="body-text max-w-xl mx-auto mb-8">
                Discover a curated platform of excellence — programmes, resources, and experiences designed to elevate professionals and leaders across industries.
              </p>
              <a
                href="https://www.avenuesofexcellence.com/"
                target="_blank"
                rel="noopener noreferrer"
                className="btn-secondary arrow-hover inline-flex"
                data-testid="avenues-cta"
              >
                Explore Avenues of Excellence <ExternalLink className="w-4 h-4" />
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="section-padding relative overflow-hidden" style={{ background: 'linear-gradient(135deg, #E84993 0%, #B565CC 50%, #9B4FBF 100%)' }} data-testid="events-cta">
        <div className="container-padding relative">
          <div className="max-w-2xl mx-auto text-center">
            <span className="reveal text-white/80 text-sm font-medium mb-6 inline-flex items-center gap-2"><Sparkles className="w-4 h-4" /> Host an Event</span>
            <h2 className="reveal reveal-delay-1 text-3xl md:text-4xl lg:text-5xl font-medium text-white mb-6" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>
              Bring Natasha to Your Event
            </h2>
            <p className="reveal reveal-delay-2 text-white/80 mb-10 max-w-lg mx-auto">
              Keynotes, workshops, and transformational experiences tailored for your audience.
            </p>
            <button onClick={() => openForm('speaking')} className="reveal reveal-delay-3 inline-flex items-center justify-center gap-2 px-8 py-4 bg-white text-[#E84993] text-xs font-medium tracking-[0.15em] uppercase rounded-full hover:-translate-y-0.5 transition-all hover:shadow-lg arrow-hover" data-testid="events-bottom-cta">
              Inquire About Speaking <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      </section>
    </main>
  );
};

/* Featured event card — large, editorial */
const EventCardFeatured = ({ event }) => {
  const cat = CATEGORY_STYLES[event.category] || CATEGORY_STYLES.leadership;
  const CatIcon = cat.icon;
  return (
    <div className="group rounded-3xl overflow-hidden bg-white border border-[#F0E6F6] hover:border-[#E84993]/30 transition-all duration-500 hover:shadow-xl hover:shadow-[#E84993]/5" data-testid="featured-event-card">
      <div className="grid grid-cols-1 lg:grid-cols-2">
        <div className="relative aspect-[4/3] lg:aspect-auto overflow-hidden">
          {event.image ? (
            <img src={event.image} alt={event.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
          ) : (
            <div className="w-full h-full bg-cream flex items-center justify-center">
              <CatIcon className="w-16 h-16 text-[#E84993]/20" />
            </div>
          )}
          <div className="absolute top-4 left-4">
            <span className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[10px] font-medium tracking-wider uppercase ${cat.bg} ${cat.text}`}>
              <CatIcon className="w-3 h-3" /> {event.category.replace('_', ' ')}
            </span>
          </div>
        </div>
        <div className="p-8 md:p-10 lg:p-12 flex flex-col justify-center">
          <h3 className="text-2xl md:text-3xl font-medium mb-4" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>
            {event.title}
          </h3>
          <div className="space-y-2 mb-6 text-sm text-[#6A6A6A]">
            <div className="flex items-center gap-2"><Calendar className="w-4 h-4 text-[#E84993]" /> {formatDate(event.date)}</div>
            {event.time && <div className="flex items-center gap-2"><Clock className="w-4 h-4 text-[#E84993]" /> {event.time}</div>}
            {event.location && <div className="flex items-center gap-2"><MapPin className="w-4 h-4 text-[#E84993]" /> {event.location}</div>}
          </div>
          <p className="body-text mb-8">{event.description}</p>
          {event.external_url ? (
            <a href={event.external_url} target="_blank" rel="noopener noreferrer" className="btn-primary arrow-hover self-start">
              {event.cta_text || 'Learn More'} <ArrowRight className="w-4 h-4" />
            </a>
          ) : (
            <span className="btn-primary self-start cursor-default opacity-80">
              {event.cta_text || 'Coming Soon'}
            </span>
          )}
        </div>
      </div>
    </div>
  );
};

/* Standard event card */
const EventCard = ({ event, isPast }) => {
  const cat = CATEGORY_STYLES[event.category] || CATEGORY_STYLES.leadership;
  const CatIcon = cat.icon;
  return (
    <div className={`group rounded-2xl overflow-hidden bg-white border border-[#F0E6F6] hover:border-[#E84993]/30 transition-all duration-500 hover:shadow-lg hover:shadow-[#E84993]/5 hover:-translate-y-1 ${isPast ? 'opacity-75' : ''}`} data-testid="event-card">
      <div className="relative aspect-[16/10] overflow-hidden">
        {event.image ? (
          <img src={event.image} alt={event.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
        ) : (
          <div className="w-full h-full bg-cream flex items-center justify-center">
            <CatIcon className="w-12 h-12 text-[#E84993]/20" />
          </div>
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent" />
        <div className="absolute top-3 left-3">
          <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[10px] font-medium tracking-wider uppercase ${cat.bg} ${cat.text}`}>
            {event.category.replace('_', ' ')}
          </span>
        </div>
        {isPast && (
          <div className="absolute top-3 right-3">
            <span className="px-3 py-1 rounded-full text-[10px] font-medium tracking-wider uppercase bg-white/90 text-[#6A6A6A]">Past</span>
          </div>
        )}
      </div>
      <div className="p-6">
        <h3 className="text-lg font-medium mb-3 line-clamp-2" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>
          {event.title}
        </h3>
        <div className="space-y-1.5 mb-4 text-xs text-[#9A9A9A]">
          <div className="flex items-center gap-1.5"><Calendar className="w-3.5 h-3.5 text-[#E84993]" /> {formatDate(event.date)}</div>
          {event.location && <div className="flex items-center gap-1.5"><MapPin className="w-3.5 h-3.5 text-[#E84993]" /> {event.location}</div>}
        </div>
        <p className="text-sm text-[#6A6A6A] line-clamp-2 mb-5">{event.description}</p>
        {event.external_url && !isPast ? (
          <a href={event.external_url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 text-xs font-medium tracking-[0.1em] uppercase text-[#E84993] hover:gap-3 transition-all">
            {event.cta_text || 'Learn More'} <ArrowRight className="w-3.5 h-3.5" />
          </a>
        ) : !isPast ? (
          <span className="text-xs font-medium tracking-[0.1em] uppercase text-[#E84993]">{event.cta_text || 'Coming Soon'}</span>
        ) : null}
      </div>
    </div>
  );
};

export default EventsPage;
